import React, { FC, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { BaseStyle, BaseStyleEnums, Tab } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import CommonMessages from '~/core/commonMessages';
import { PlayerPosition } from '~/core/enums';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';

// import { PlayerNews } from './components/playerNews/PlayerNews';
import { usePlayerBioQuery, usePlayerGameLogQuery, usePlayerQuery } from './api';
import { PlayerBio } from './components/PlayerBio';
import { PlayerAwards } from './components/playerAwards/PlayerAwards';
import { PlayerHeaderComponent } from './components/playerHeader/PlayerHeaderComponent';
import { LastFiveGames } from './components/playerStats/LastFiveGames';
import { PlayerCareerStats } from './components/playerStats/PlayerCareerStats';
import { PlayerGameLogs } from './components/playerStats/PlayerGameLogs';

import { AdContainer, PlayerSectionHeader, SmallPlayerBorder, StyledTabBar } from './styles';

enum PlayerStatsViews {
    CAREER = 'CAREER',
    GAME_LOGS = 'GAME_LOGS',
}

const Player: FC = () => {
    const [selectedTab, setSelectedTab] = useState<string>(PlayerStatsViews.CAREER);
    const screenSize = useSizeWithThrottle();
    const { player: playerParam } = useParams();
    const intl = useIntl();

    // player param can have a slug in it, so this just gets the id part out
    const playerId = playerParam?.match(/[0-9]{7}/)?.[0];

    const { data: player, isLoading } = usePlayerQuery(playerId);

    const { data: gameLog, hasLoaded: hasGameLogLoaded } = usePlayerGameLogQuery(
        playerId,
        undefined,
        undefined,
        playerId !== undefined
    );

    const showLast5Games = player && player.last5Games && player.currentTeamId;

    const { data: playerBio } = usePlayerBioQuery(playerId);
    // TODO: player news is hidden until further notice :(
    // const { data: playerNews, isLoading: isLoadingNews } = usePlayerNewsQuery(playerId, player?.currentTeamId);

    const isSmallScreen = screenSize.width <= BaseStyleEnums.ScreenWidthBreakpoints.small;

    // TODO: Make use of meta endpoint (https://issues.nhl.com/browse/ICE-314) for club name
    const pageTitle = useMemo(
        () =>
            intl.formatMessage(
                {
                    description: 'Title for the Player page, to be displayed on a browser tab etc.',
                    defaultMessage: '{firstName} {lastName} Stats And News | NHL.com',
                },
                {
                    firstName: parseValueFromLocalizedAttribute(player.firstName),
                    lastName: parseValueFromLocalizedAttribute(player.lastName),
                }
            ),
        [intl, player]
    );

    const playerTabs = [
        {
            key: PlayerStatsViews.CAREER,
            label: intl.formatMessage(CommonMessages.tableHeaders.long.career),
            ariaControlId: `${PlayerStatsViews.CAREER}-tabpanel`,
        },
    ];

    if (gameLog?.playerStatsSeasons?.length > 0) {
        playerTabs.push({
            key: PlayerStatsViews.GAME_LOGS,
            label: intl.formatMessage(CommonMessages.tableHeaders.long.gameLogs),
            ariaControlId: `${PlayerStatsViews.GAME_LOGS}-tabpanel`,
        });
    }

    const handleOnChangeSelectedTab = (tab: Tab) => {
        setSelectedTab(tab.key);
    };

    return isLoading ? (
        <LoadingDisplay isLoading={true} />
    ) : (
        <PageWrapper theme={BaseStyle.LightTheme} pageTitle={pageTitle} defaultPageLayout hasExternalBackground>
            <PlayerHeaderComponent player={player} />

            {isSmallScreen ? null : (
                <AdContainer>
                    {window.__AD_SETTINGS__.leaderDivIds?.length > 0 && (
                        <AdTag divId={window.__AD_SETTINGS__.leaderDivIds[1]} adType={AdType.LEADER} />
                    )}
                </AdContainer>
            )}

            <SmallPlayerBorder />

            {showLast5Games && <LastFiveGames player={player} />}

            <PlayerSectionHeader aria-level={2}>
                <FormattedMessage defaultMessage="Stats" description="A header describing a player's stats" />
            </PlayerSectionHeader>

            <StyledTabBar tabs={playerTabs} onChangeSelectedTab={handleOnChangeSelectedTab} />

            {/* abstracting these tab panels into functional components causes lots of disruptive re-renders of their children, so we don't do that */}
            <div
                role="tabpanel"
                hidden={selectedTab !== PlayerStatsViews.CAREER}
                id={`${PlayerStatsViews.CAREER}-tabpanel`}
                aria-labelledby={PlayerStatsViews.CAREER}
            >
                <PlayerCareerStats player={player} />
            </div>

            <div
                role="tabpanel"
                hidden={selectedTab !== PlayerStatsViews.GAME_LOGS}
                id={`${PlayerStatsViews.GAME_LOGS}-tabpanel`}
                aria-labelledby={PlayerStatsViews.GAME_LOGS}
            >
                {hasGameLogLoaded ? (
                    <PlayerGameLogs
                        gameLog={gameLog}
                        playerId={playerId}
                        isGoalie={player?.position === PlayerPosition.goalie}
                    />
                ) : (
                    <LoadingDisplay isLoading isInline />
                )}
            </div>

            <AdContainer>
                {window.__AD_SETTINGS__.leaderDivIds?.length > 0 && (
                    <AdTag
                        key={window.__AD_SETTINGS__.leaderDivIds[2]}
                        divId={window.__AD_SETTINGS__.leaderDivIds[2]}
                        adType={AdType.LEADER}
                    />
                )}
            </AdContainer>

            {/**
             * The bulk of the player news functionality exist, we're just waiting to hear back if we want to keep this
             * functionality or remove it. Some modifications like article linking may be needed as things have changed
                {isLoadingNews ? (
                <CenteredLoadingDisplay>
                    <LoadingDisplay isLoading isInline />
                </CenteredLoadingDisplay>
            ) : (
                playerNews?.items?.length > 0 && <PlayerNews news={playerNews} player={player} />
            )}*/}

            {player?.awards !== undefined && <PlayerAwards player={player} showAwards />}

            <PlayerBio bio={playerBio?.items?.[0]?.fields} player={player} />
        </PageWrapper>
    );
};

export { Player as Component };
